import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import IconButton from '../../Button/IconButton';
import { cn } from '../../../../lib/utils';

/**
 * FormModal - Organism component for modal forms
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.maxWidth - Max width class (e.g., 'max-w-md', 'max-w-lg')
 * @param {string} props.className - Additional CSS classes for the content container
 * @param {string} props.titleClassName - Additional CSS classes for the title
 */
export const FormModal = ({
    isOpen,
    onClose,
    title,
    children,
    maxWidth = 'max-w-md',
    className = '',
    titleClassName = '',
    showCloseButton = true
}) => {
    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-neutral-green-600 bg-opacity-30 backdrop-blur-md"
                onClick={onClose}
            />

            {/* Modal Container */}
            <div className={`relative z-10 w-full ${maxWidth} max-h-[90vh] overflow-hidden rounded-[21px]`}>
                {/* Scrollable Content */}
                <div className={cn("bg-red-50 rounded-[21px] overflow-y-auto max-h-[90vh] shadow-2xl", className)}>
                    <div className="p-6 md:p-8 space-y-5">
                        {/* Close Button */}
                        {showCloseButton && (
                            <div className="absolute top-4 right-4 z-20">
                                <IconButton
                                    icon={<X />}
                                    variant="ghost"
                                    size="sm"
                                    onClick={onClose}
                                />
                            </div>
                        )}

                        {/* Title */}
                        {title && (
                            <h1 className={cn("text-2xl font-bold text-black text-center leading-7", titleClassName)}>
                                {title}
                            </h1>
                        )}

                        {/* Content */}
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormModal;
